import React, { useLayoutEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { AutoAwesome, Check, Close, DoneAll, NavigateBefore, NavigateNext } from '@mui/icons-material';

/**
 * Sticky bar shown while AI changes are under review.
 * @param {{changes: Array<{id: string, kind: string, summary: string}>, activeIndex: number, label?: string, onPrev: Function, onNext: Function, onAccept: Function, onReject: Function, onAcceptAll: Function, onRejectAll: Function}} props - Props.
 * @returns {JSX.Element}
 */
export const ReviewBar = ({ changes, activeIndex, label, onPrev, onNext, onAccept, onReject, onAcceptAll, onRejectAll }) => {
  const active = changes[activeIndex];
  return (
    <Paper
      elevation={0}
      role="region"
      aria-label="Review AI changes"
      sx={{
        position: 'sticky',
        top: 'var(--q-editor-sticky-top, 0px)',
        zIndex: 6,
        mb: 1,
        px: 1.5,
        py: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        borderRadius: 'var(--q-radius-lg, 12px)',
        border: '1px solid',
        borderColor: 'var(--q-accent, #4F3BD6)',
        bgcolor: 'var(--q-surface, #FFFFFF)',
        boxShadow: 'var(--q-shadow-md)',
      }}
    >
      <Chip
        size="small"
        icon={<AutoAwesome sx={{ fontSize: 16 }} />}
        label={label || 'AI changes'}
        sx={{ bgcolor: 'var(--q-accent-soft, #ECE8FC)', color: 'var(--q-accent-strong, #2F2096)', fontWeight: 600 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {changes.length ? `Change ${activeIndex + 1} of ${changes.length}` : 'No changes left'}
      </Typography>
      <Tooltip title="Previous change (Alt+[)">
        <span>
          <IconButton size="small" aria-label="Previous change" onClick={onPrev} disabled={changes.length <= 1}>
            <NavigateBefore fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Next change (Alt+])">
        <span>
          <IconButton size="small" aria-label="Next change" onClick={onNext} disabled={changes.length <= 1}>
            <NavigateNext fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {active && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: '1 1 160px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={active.summary}
        >
          {active.summary}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto', flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" color="inherit" startIcon={<Close />} onClick={onRejectAll}>
          Reject all
        </Button>
        <Button size="small" variant="contained" disableElevation startIcon={<DoneAll />} onClick={onAcceptAll}>
          Accept all
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', fontSize: 12, color: 'text.secondary' }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" className="q-diff-delete" sx={{ px: 0.5, borderRadius: '4px' }}>removed</Box>
        </Box>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" className="q-diff-insert" sx={{ px: 0.5, borderRadius: '4px' }}>added</Box>
        </Box>
        {active && (
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' }, ml: 'auto' }}>
            <Button size="small" onClick={() => onReject(active.id)}>Reject</Button>
            <Button size="small" onClick={() => onAccept(active.id)}>Accept</Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

/**
 * Floating accept/reject chip pinned next to the active change inside the page.
 * @param {{editor: import('@tiptap/core').Editor, container: HTMLElement|null, change: {id: string}|null, onAccept: Function, onReject: Function, version: number}} props - Props.
 * @returns {JSX.Element|null}
 */
export const ReviewChangeChip = ({ editor, container, change, onAccept, onReject, version }) => {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed || !container || !change) {
      setPos(null);
      return undefined;
    }
    const root = editor.view.dom;
    const selector = `[data-change-id="${String(change.id).replace(/[^\w-]/g, '')}"]`;
    const update = () => {
      root.querySelectorAll('.q-review-active').forEach((el) => el.classList.remove('q-review-active'));
      const elements = Array.from(root.querySelectorAll(selector));
      elements.forEach((el) => el.classList.add('q-review-active'));
      const first = elements[0];
      if (!first) {
        setPos(null);
        return;
      }
      const rect = first.getBoundingClientRect();
      const host = container.getBoundingClientRect();
      const width = 176;
      setPos({
        top: Math.max(0, rect.top - host.top - 40),
        left: Math.max(8, Math.min(rect.left - host.left, host.width - width - 8)),
      });
    };
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(container);
    window.addEventListener('resize', update);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', update);
      root.querySelectorAll('.q-review-active').forEach((el) => el.classList.remove('q-review-active'));
    };
  }, [editor, container, change, version]);

  if (!pos || !change) return null;
  return (
    <Paper
      elevation={0}
      contentEditable={false}
      sx={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        zIndex: 4,
        display: { xs: 'none', md: 'flex' },
        gap: 0.5,
        p: 0.5,
        borderRadius: '10px',
        bgcolor: 'var(--q-tooltip-bg)',
        boxShadow: 'var(--q-shadow-xl)',
      }}
    >
      <Tooltip title="Reject (Ctrl+Backspace)">
        <Button
          size="small"
          onClick={() => onReject(change.id)}
          startIcon={<Close sx={{ fontSize: 16 }} />}
          sx={{ color: 'var(--q-tooltip-text)', minWidth: 0, px: 1, height: 28, '&:hover': { bgcolor: 'color-mix(in srgb, var(--q-tooltip-text) 12%, transparent)' } }}
        >
          Reject
        </Button>
      </Tooltip>
      <Tooltip title="Accept (Ctrl+Enter)">
        <Button
          size="small"
          onClick={() => onAccept(change.id)}
          startIcon={<Check sx={{ fontSize: 16 }} />}
          sx={{ color: 'var(--q-tooltip-text)', minWidth: 0, px: 1, height: 28, '&:hover': { bgcolor: 'color-mix(in srgb, var(--q-tooltip-text) 12%, transparent)' } }}
        >
          Accept
        </Button>
      </Tooltip>
    </Paper>
  );
};
